import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import type { ApiClientDetail, ApiClientRecord, ApiClientsResponse } from '@/lib/adapters/clients';

interface ClientFilters {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/** Clients, optionally filtered. `enabled: false` holds the request, e.g. until a search term is long enough. */
export function useClients(filters: ClientFilters = {}, options: { enabled?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  const query = params.toString();
  const path = `/clients${query ? `?${query}` : ''}`;

  return useQuery({
    queryKey: ['clients', filters],
    queryFn: () => api.get<ApiClientsResponse>(path),
    enabled: options.enabled ?? true,
    // Changing a filter keeps the current list on screen until the filtered one arrives.
    placeholderData: keepPreviousData,
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: () => api.get<ApiClientDetail>(`/clients/${id}`),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ApiClientRecord>) => api.post<ApiClientRecord>('/clients', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client created');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to create client'),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<ApiClientRecord>) =>
      api.patch<ApiClientRecord>(`/clients/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client updated');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to update client'),
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/clients/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client deleted');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to delete client'),
  });
}
